export interface ProblemDetails {
  type?: string;
  title: string;
  status: number;
  detail?: string;
  traceId?: string;
  errors?: Record<string, string[]>;
}

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
const ACCESS_TOKEN_KEY = 'vendiz.accessToken';
const REFRESH_TOKEN_KEY = 'vendiz.refreshToken';

function readSessionValue(key: string) {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
}

export function hasStoredSession() {
  return Boolean(readSessionValue(ACCESS_TOKEN_KEY));
}

export class ApiError extends Error {
  constructor(public problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
  }
}

export function resolveApiUrl(url: string) {
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE_URL}${url.startsWith('/') ? url : `/${url}`}`;
}

type RequestOptions = RequestInit & { retryAfterRefresh?: boolean };
let refreshRequest: Promise<boolean> | null = null;

export function storeSession(payload: { accessToken: string; refreshToken: string }, remember = true) {
  clearSession();
  const storage = remember ? localStorage : sessionStorage;
  storage.setItem(ACCESS_TOKEN_KEY, payload.accessToken);
  storage.setItem(REFRESH_TOKEN_KEY, payload.refreshToken);
}

export function clearSession() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
}

function expireSession() {
  clearSession();
  window.dispatchEvent(new Event('vendiz:session-expired'));

  const loginPath = window.location.pathname.startsWith('/portal') ? '/portal/login' : '/login';
  if (window.location.pathname !== loginPath) window.location.replace(loginPath);
}

async function refreshSession(): Promise<boolean> {
  const remember = Boolean(localStorage.getItem(REFRESH_TOKEN_KEY));
  const refreshToken = readSessionValue(REFRESH_TOKEN_KEY);
  if (!refreshToken) return false;
  try {
    const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) return false;
    const session = await response.json() as { accessToken: string; refreshToken: string };
    storeSession(session, remember);
    return true;
  } catch {
    return false;
  }
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { retryAfterRefresh = true, ...init } = options;
  const token = readSessionValue(ACCESS_TOKEN_KEY);
  const isFormData = init.body instanceof FormData;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init.headers,
    },
  });

  if (response.status === 401 && token && path !== '/api/v1/auth/login' && path !== '/api/v1/auth/refresh') {
    if (retryAfterRefresh) {
      refreshRequest ??= refreshSession().finally(() => { refreshRequest = null; });
      if (await refreshRequest) return request<T>(path, { ...init, retryAfterRefresh: false });
    }
    expireSession();
  }

  if (!response.ok) {
    const problem = await response.json().catch(() => ({ title: 'Request failed', status: response.status })) as ProblemDetails;
    throw new ApiError(problem);
  }
  if (response.status === 204) return undefined as T;
  const responseText = await response.text();
  return (responseText ? JSON.parse(responseText) : undefined) as T;
}

async function requestBlob(path: string, retryAfterRefresh = true): Promise<Blob> {
  const token = readSessionValue(ACCESS_TOKEN_KEY);
  const response = await fetch(resolveApiUrl(path), { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (response.status === 401 && token) {
    if (retryAfterRefresh) {
      refreshRequest ??= refreshSession().finally(() => { refreshRequest = null; });
      if (await refreshRequest) return requestBlob(path, false);
    }
    expireSession();
  }
  if (!response.ok) {
    const problem = await response.json().catch(() => ({ title: 'Download failed', status: response.status })) as ProblemDetails;
    throw new ApiError(problem);
  }
  return response.blob();
}

type QueryValue = string | number | boolean | undefined | null;

export function toQuery(params: Record<string, QueryValue | QueryValue[]>) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    const values = Array.isArray(value) ? value : [value];
    values.forEach((item) => {
      if (item !== undefined && item !== null && item !== '') query.append(key, String(item));
    });
  });
  const value = query.toString();
  return value ? `?${value}` : '';
}

export const http = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown, headers?: HeadersInit) => request<T>(path, { method: 'POST', headers, body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body) }),
  put: <T>(path: string, body: unknown, headers?: HeadersInit) => request<T>(path, { method: 'PUT', headers, body: body instanceof FormData ? body : JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  blob: (path: string) => requestBlob(path),
};
